'use client';

import { useEffect, useRef } from 'react';
import useWalletIsConnected from '../useWalletIsConnected';
import usePostHogCapture from './usePostHogCapture';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import useCurrentWalletAdapter from '../useCurrentWalletAdapter';
import posthog from 'posthog-js';
import Env from '../../environmentVariables/EnvironmentVariables';
import { useSettingsContext } from '../../providers/settingsProvider';
import { getSanitizedUserSettingsForPosthog } from './userSettingsForPosthog';
import useLayoutConfigSetting from '../useLayoutConfigSetting';
import useNotificationPlacementSetting from '../useNotificationPlacementSetting';
import { POSTHOG_UTILS } from '../../providers/posthog/postHogProvider';
import useAccountExists from '../useAccountExists';
import useDriftAccountStore from '../../stores/useDriftAccountsStore';
import { usePrivyEmbeddedWalletWithFallback } from '../wallets/privy/mappings/usePrivyEmbeddedWalletWithFallback';
import { DEFAULT_PUBLIC_KEY } from 'src/@types/types';
import { DynamicSuperProperties } from '@drift-labs/posthog-types';

const usePostHogIdentity = () => {
	const connected = useWalletIsConnected();
	const authority = useDriftStore((s) => s.wallet?.currentPublicKeyString);
	const isMagicAuth = useDriftStore((s) => s.wallet?.isMagicAuth);
	const walletAdapter = useCurrentWalletAdapter();
	const { currentSettings } = useSettingsContext();
	const [layoutConfig] = useLayoutConfigSetting();
	const [notificationPlacement] = useNotificationPlacementSetting();
	const walletName = isMagicAuth
		? 'Magic Auth'
		: walletAdapter?.name?.toString();
	const isE2eWallet = walletName === Env.E2E_WALLET.name;
	const accountExists = useAccountExists();
	const isPrivyNativeAuth = useDriftStore((s) => s.wallet.isPrivyNativeAuth);
	const userPubKey = useDriftAccountStore(
		(s) => s?.accounts?.[s?.currentUserKey ?? '']?.pubKey
	);
	const delegate = useDriftAccountStore(
		(s) => s?.accounts?.[s?.currentUserKey ?? '']?.delegate
	);
	const embeddedWallet = usePrivyEmbeddedWalletWithFallback();
	const isDelegated = !!delegate && !delegate.equals(DEFAULT_PUBLIC_KEY);
	const isDelegatedToEmbeddedWallet =
		isDelegated && !!embeddedWallet && delegate.equals(embeddedWallet);

	const wasPreviouslyConnectedRef = useRef(false);

	useEffect(() => {
		if (isE2eWallet) {
			return;
		}

		const isLocalHost = window.location.href.includes('localhost');
		if (isLocalHost) {
			// Skip capturing events during local development
			return;
		}

		if (connected && authority) {
			if (!wasPreviouslyConnectedRef.current) {
				posthog.identify(authority, {
					...getSanitizedUserSettingsForPosthog(currentSettings),
					layoutConfig,
					notificationPlacement,
				});
			}
			wasPreviouslyConnectedRef.current = true;
		} else {
			if (wasPreviouslyConnectedRef.current) {
				posthog.reset();
				// posthog.reset() clears all super properties — re-register static ones
				POSTHOG_UTILS.registerStaticSuperProperties();
			}
			wasPreviouslyConnectedRef.current = false;
		}
	}, [
		connected,
		authority,
		isE2eWallet,
		currentSettings,
		layoutConfig,
		notificationPlacement,
	]);

	useEffect(() => {
		if (isE2eWallet) return;

		posthog.register({
			connected,
			account_exists: connected
				? accountExists === undefined
					? null
					: accountExists
				: null,
			authority: connected ? authority : null,
			wallet_name: connected ? walletName : null,
			is_privy_native: connected ? !!isPrivyNativeAuth : null,
			is_delegated_to_embedded_wallet: connected
				? isDelegatedToEmbeddedWallet
				: null,
			user: connected ? (userPubKey?.toBase58() ?? null) : null,
		} satisfies DynamicSuperProperties);
	}, [
		connected,
		authority,
		walletName,
		accountExists,
		isE2eWallet,
		isPrivyNativeAuth,
		isDelegatedToEmbeddedWallet,
		userPubKey,
	]);
};

const usePostHogGlobalEvents = (): null => {
	const connected = useWalletIsConnected();
	const wallet = useCurrentWalletAdapter();
	const { captureEvent } = usePostHogCapture();

	usePostHogIdentity();

	// Effect to catch wallet connections
	useEffect(() => {
		if (!connected) return;
		if (!wallet?.name) return;

		captureEvent('onboarding_wallet_connected', {
			name: wallet.name,
		});
	}, [connected, wallet?.name]);

	return null;
};

export default usePostHogGlobalEvents;
